import { getLand, deleteLand, updateLand, addLand } from '@/adapters/master';
import { ILand, ILandRes, IApiResponse } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  notification,
  Radio,
  Table,
  TableColumnsType,
  Input,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { uniqueId } from 'lodash';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { errors?: { errorMessage?: string } };
}

type LandBatchData = Partial<ILand>[];

const getErrorMessage = (r: unknown): string => {
  if (r instanceof Error) return r.message;
  if (typeof r === 'object' && r !== null && 'message' in r)
    return (r as { message: string }).message;
  return 'เกิดข้อผิดพลาดในการดำเนินการ';
};

const LandPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ILand[]>([]);
  const [selected, setSelected] = useState<ILand[]>([]);
  const [tab, setTab] = useState<'create' | 'update'>('create');
  const [searchText, setSearchText] = useState('');

  const { data, refetch, isFetching } = useQuery<ILandRes, Error>({
    queryKey: ['get-land'],
    queryFn: getLand,
    retry: 0,
  });

  const notifyBatchErrors = (
    title: string,
    batchResults: IBatchRejection[],
    originalData: LandBatchData,
  ) => {
    const errors: string[] = [];
    if (Array.isArray(batchResults)) {
      batchResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          const failedCode = originalData[index]?.FARM_ID || `Index: ${index}`;
          errors.push(
            `${failedCode}: ${result.reason?.errors?.errorMessage || 'ไม่ทราบสาเหตุ'}`,
          );
        }
      });
    }
    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: (
          <ul
            style={{
              paddingLeft: '15px',
              margin: 0,
              maxHeight: '250px',
              overflowY: 'auto',
            }}
          >
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        ),
        duration: 8,
      });
      return true;
    }
    return false;
  };

  const actionLandMutation = useMutation({
    mutationFn: (payload: LandBatchData) =>
      tab === 'create' ? addLand(payload) : updateLand(payload),
    onSuccess: (res, variables) => {
      if (
        !notifyBatchErrors(
          tab === 'create' ? 'Sync Land ไม่สำเร็จ' : 'Update Land ไม่สำเร็จ',
          res.data as IBatchRejection[],
          variables,
        )
      ) {
        notification.success({
          message: tab === 'create' ? 'Sync Land สำเร็จ' : 'Update Land สำเร็จ',
        });
      }
      refetch();
    },
    onError: r => notification.error({ message: getErrorMessage(r) }),
  });

  const actionDeleteLand = useMutation({
    mutationFn: (payload: LandBatchData) => deleteLand(payload),
    onSuccess: (res, variables) => {
      if (
        !notifyBatchErrors(
          'ลบข้อมูลไม่สำเร็จ',
          res.data as IBatchRejection[],
          variables,
        )
      ) {
        notification.success({ message: 'Delete Land สำเร็จ' });
      }
      refetch();
    },
    onError: r => notification.error({ message: getErrorMessage(r) }),
  });

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lowerSearch = searchText.toLowerCase();
    return selected.filter(
      item =>
        item.FARM_USER1?.toLowerCase().includes(lowerSearch) ||
        item.FRM_FNAME?.toLowerCase().includes(lowerSearch) ||
        item.FRM_LNAME?.toLowerCase().includes(lowerSearch) ||
        item.FARM_ID?.toLowerCase().includes(lowerSearch),
    );
  }, [selected, searchText]);

  const columns: TableColumnsType<ILand> = useMemo(
    () => [
      {
        title: 'ลำดับ',
        width: 60,
        align: 'center',
        render: (_, __, i) => <b>{i + 1}</b>,
      },
      {
        title: 'รหัสเกษตรกร',
        dataIndex: 'FARM_USER1',
        width: 130,
        sorter: (a, b) =>
          (a.FARM_USER1 || '').localeCompare(b.FARM_USER1 || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_USER1).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_USER1 === value,
        filterSearch: true,
      },
      {
        title: 'ชื่อ',
        dataIndex: 'FRM_FNAME',
        width: 150,
        sorter: (a, b) => (a.FRM_FNAME || '').localeCompare(b.FRM_FNAME || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FRM_FNAME).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FRM_FNAME === value,
        filterSearch: true,
      },
      {
        title: 'นามสกุล',
        dataIndex: 'FRM_LNAME',
        width: 150,
        sorter: (a, b) => (a.FRM_LNAME || '').localeCompare(b.FRM_LNAME || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FRM_LNAME).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FRM_LNAME === value,
        filterSearch: true,
      },
      {
        title: 'แปลงที่',
        dataIndex: 'FARM_ID',
        width: 100,
        sorter: (a, b) => (a.FARM_ID || '').localeCompare(b.FARM_ID || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_ID).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_ID === value,
        filterSearch: true,
      },
      {
        title: 'ตำบลที่ตั้งสวน',
        dataIndex: 'FARM_DISTRICT',
        width: 140,
        sorter: (a, b) =>
          (a.FARM_DISTRICT || '').localeCompare(b.FARM_DISTRICT || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_DISTRICT).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_DISTRICT === value,
        filterSearch: true,
      },
      {
        title: 'อำเภอที่ตั้งสวน',
        dataIndex: 'FARM_AMPHUR',
        width: 140,
        sorter: (a, b) =>
          (a.FARM_AMPHUR || '').localeCompare(b.FARM_AMPHUR || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_AMPHUR).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_AMPHUR === value,
        filterSearch: true,
      },
      {
        title: 'จังหวัดที่ตั้งสวน',
        dataIndex: 'FARM_PROVINCE',
        width: 140,
        sorter: (a, b) =>
          (a.FARM_PROVINCE || '').localeCompare(b.FARM_PROVINCE || ''),
        filters: Array.from(
          new Set(selected.map(i => i.FARM_PROVINCE).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.FARM_PROVINCE === value,
        filterSearch: true,
      },
      {
        title: 'จำนวนไร่',
        dataIndex: 'FARM_AREA_FARM',
        width: 100,
        align: 'right',
        render: val => (val ? Number(val).toLocaleString() : '0'),
        sorter: (a, b) => Number(a.FARM_AREA_FARM) - Number(b.FARM_AREA_FARM),
      },
    ],
    [selected],
  );

  useEffect(() => {
    const listMap = {
      create: data?.insert,
      update: data?.update,
      match: data?.match,
    };
    const currentList = listMap[tab] || [];
    setSelected(
      currentList.map(c => ({ ...c, _key: c._key || uniqueId('land-') })),
    );
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, data]);

  const isPending = actionLandMutation.isPending || actionDeleteLand.isPending;

  return (
    <div style={{ padding: '20px' }}>
      <Flex justify="space-between" align="center" className="mb-3">
        <Radio.Group
          value={tab}
          onChange={e => setTab(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="create">เพิ่ม</Radio.Button>
          <Radio.Button value="update">แก้ไข/ลบ</Radio.Button>
        </Radio.Group>
        <Flex gap={12}>
          <Input.Search
            placeholder="ค้นหา (รหัส/ชื่อ/นามสกุล/แปลงที่)"
            allowClear
            onSearch={setSearchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 350 }}
          />
          {tab === 'update' && (
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={!selectedRowKeys.length || isPending}
              onClick={() => actionDeleteLand.mutate(selectedRows)}
            >
              ลบข้อมูล Land
            </button>
          )}
          <button
            type="button"
            className="btn btn-success"
            disabled={!selectedRowKeys.length || isPending}
            onClick={() => actionLandMutation.mutate(selectedRows)}
          >
            {isPending
              ? 'กำลังดำเนินการ...'
              : tab === 'create'
                ? 'ส่งข้อมูลไป OTOD >'
                : 'แก้ไขข้อมูลที่ OTOD >'}
          </button>
        </Flex>
      </Flex>
      <Table<ILand>
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        columns={columns}
        dataSource={filteredData}
        loading={isFetching}
        size="small"
        bordered
        rowKey="_key"
        pagination={false}
        scroll={{ x: 1200 }}
        sticky={{ offsetHeader: 0 }}
      />
    </div>
  );
};

export default LandPage;
